/**
 * Clerk Authentication Configuration
 *
 * Centralizes Clerk setup and provides role-based access control helpers
 * for the AxLabs virtual laboratory platform.
 */

// Role types for user authorization
export type UserRole = 'student' | 'teacher' | 'institution_admin' | 'super_admin';

/**
 * Checks if Clerk is properly configured with a publishable key
 */
export function isClerkConfigured(): boolean {
  return Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);
}

/**
 * Mock user for development mode when Clerk is not configured
 */
export const DEV_MOCK_USER = {
  id: 'dev-mock-user',
  publicMetadata: { role: 'super_admin' as UserRole },
  primaryEmailAddress: { emailAddress: 'dev@localhost' },
  firstName: 'Dev',
  lastName: 'User',
  fullName: 'Dev User',
};

// Metadata key for storing user role in Clerk public metadata
const ROLE_METADATA_KEY = 'role';

/**
 * Gets the user role from Clerk user metadata
 *
 * @param user - The Clerk user object (or null if not authenticated)
 * @returns The user's role, or 'student' as default for authenticated users without a role
 */
export function getUserRole(user: { publicMetadata?: Record<string, unknown> } | null | undefined): UserRole {
  if (!user?.publicMetadata) {
    return 'student';
  }

  const role = user.publicMetadata[ROLE_METADATA_KEY];

  // Validate that the role is one of our expected types
  if (isValidRole(role)) {
    return role;
  }

  // Default to student role for authenticated users
  return 'student';
}

/**
 * Type guard to validate if a value is a valid UserRole
 */
function isValidRole(value: unknown): value is UserRole {
  return (
    value === 'student' ||
    value === 'teacher' ||
    value === 'institution_admin' ||
    value === 'super_admin'
  );
}

/**
 * Checks if the user has teacher privileges or higher
 *
 * @param user - The Clerk user object (or null if not authenticated)
 * @returns true if user is a teacher, institution admin, or super admin
 */
export function isTeacher(user: { publicMetadata?: Record<string, unknown> } | null | undefined): boolean {
  const role = getUserRole(user);
  return role === 'teacher' || role === 'institution_admin' || role === 'super_admin';
}

/**
 * Checks if the user has admin privileges
 *
 * @param user - The Clerk user object (or null if not authenticated)
 * @returns true if user is an institution admin or super admin
 */
export function isAdmin(user: { publicMetadata?: Record<string, unknown> } | null | undefined): boolean {
  const role = getUserRole(user);
  return role === 'institution_admin' || role === 'super_admin';
}

/**
 * Checks if the user has super admin privileges
 *
 * @param user - The Clerk user object (or null if not authenticated)
 * @returns true if user is a super admin
 */
export function isSuperAdmin(user: { publicMetadata?: Record<string, unknown> } | null | undefined): boolean {
  const role = getUserRole(user);
  return role === 'super_admin';
}

/**
 * Checks if the user has a specific role
 *
 * @param user - The Clerk user object (or null if not authenticated)
 * @param requiredRole - The role to check against
 * @returns true if user has the required role
 */
export function hasRole(
  user: { publicMetadata?: Record<string, unknown> } | null | undefined,
  requiredRole: UserRole
): boolean {
  return getUserRole(user) === requiredRole;
}

/**
 * Checks if the user has at least the specified role level
 *
 * Role hierarchy (lowest to highest):
 * student < teacher < institution_admin < super_admin
 *
 * @param user - The Clerk user object (or null if not authenticated)
 * @param minimumRole - The minimum role level required
 * @returns true if user's role is at least the minimum level
 */
export function hasRoleLevel(
  user: { publicMetadata?: Record<string, unknown> } | null | undefined,
  minimumRole: UserRole
): boolean {
  const roleHierarchy: Record<UserRole, number> = {
    student: 0,
    teacher: 1,
    institution_admin: 2,
    super_admin: 3,
  };

  const userRole = getUserRole(user);
  return roleHierarchy[userRole] >= roleHierarchy[minimumRole];
}
